class ShopifyDataLayerTracker {
  constructor(config = {}) {
    this.config = {
      debug: false,
      currency: "SEK",
      ...config
    };

    this.dataLayer = window.dataLayer = window.dataLayer || [];

    this.pageType = this.detectPageType();
    this.currency = this.detectCurrency();

    this.init();
  }

  /* ---------------------------------------------------------
   * Init
   * --------------------------------------------------------- */

  init() {
    this.debug("init", {
      page_type: this.pageType,
      currency: this.currency
    });

    if (this.pageType === "product") {
      this.trackViewItem();
    }

    if (this.pageType === "collection") {
      this.trackViewItemList();
      this.bindSelectItem();
    }

    if (this.pageType === "cart") {
      this.trackViewCart();
      this.bindBeginCheckout();
    }

    this.bindAddToCart();
  }

  /* ---------------------------------------------------------
   * Core emit logic
   * --------------------------------------------------------- */

  emit(eventName, payload = {}) {
    const finalPayload = {
      event: eventName,
      event_id: this.generateEventId(`${eventName}_`),
      page_type: this.pageType,
      ...payload
    };

    this.dataLayer.push(finalPayload);
    this.debug(eventName, finalPayload);
  }

  /* ---------------------------------------------------------
   * GA4 EVENTS
   * --------------------------------------------------------- */

  trackViewItem() {
    const product = window.__SHOPIFY_PRODUCT__;
    if (!product) return;

    const variant = product.selected_or_first_available_variant;

    this.emit("view_item", {
      ecommerce: {
        currency: this.currency,
        value: Number(variant.price) / 100,
        items: [
          {
            item_id: String(variant.id),
            item_name: product.title,
            item_brand: product.vendor || null,
            item_category: product.type || null,
            item_variant: variant.title,
            price: Number(variant.price) / 100,
            quantity: 1
          }
        ]
      }
    });
  }

  trackViewItemList() {
    const collection = window.__SHOPIFY_COLLECTION__;
    if (!collection || !collection.products) return;

    const items = collection.products.map((product, index) => {
      const variant = product.variants[0];
      return {
        item_id: String(variant.id),
        item_name: product.title,
        item_brand: product.vendor || null,
        item_category: product.type || null,
        item_variant: variant.title,
        price: Number(variant.price) / 100,
        index: index + 1
      };
    });

    this.emit("view_item_list", {
      ecommerce: {
        currency: this.currency,
        items
      }
    });
  }

  trackAddToCart({ product, variant, quantity }) {
    this.emit("add_to_cart", {
      ecommerce: {
        currency: this.currency,
        value: (Number(variant.price) / 100) * quantity,
        items: [
          {
            item_id: String(variant.id),
            item_name: product.title,
            item_brand: product.vendor || null,
            item_category: product.type || null,
            item_variant: variant.title,
            price: Number(variant.price) / 100,
            quantity
          }
        ]
      }
    });
  }

  trackViewCart(cart) {
    const items = cart.items.map(item => ({
      item_id: String(item.variant_id),
      item_name: item.product_title,
      item_brand: item.vendor || null,
      item_category: null,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity
    }));

    this.emit("view_cart", {
      ecommerce: {
        currency: this.currency,
        value: cart.total_price / 100,
        items
      }
    });
  }

  trackBeginCheckout(cart) {
    const items = cart.items.map(item => ({
      item_id: String(item.variant_id),
      item_name: item.product_title,
      item_brand: item.vendor || null,
      item_category: null,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity
    }));

    this.emit("begin_checkout", {
      ecommerce: {
        currency: this.currency,
        value: cart.total_price / 100,
        items
      }
    });
  }

  /* ---------------------------------------------------------
   * Bindings
   * --------------------------------------------------------- */

  bindAddToCart() {
    document.addEventListener("submit", e => {
      const form = e.target;
      if (!form.action.includes("/cart/add")) return;

      const product = window.__SHOPIFY_PRODUCT__;
      if (!product) return;

      const variantId = form.querySelector("[name='id']").value;
      const variant = product.variants.find(v => String(v.id) === variantId);
      const quantity = Number(
        form.querySelector("[name='quantity']")?.value || 1
      );

      if (variant) {
        this.trackAddToCart({ product, variant, quantity });
      }
    });
  }

  bindSelectItem() {
    document.addEventListener("click", e => {
      const link = e.target.closest("a[href*='/products/']");
      if (!link) return;

      this.emit("select_item", {
        ecommerce: {
          currency: this.currency,
          items: [
            {
              item_id: link.dataset.variantId || null,
              item_name: link.dataset.productName || null,
              index: Number(link.dataset.index) || null
            }
          ]
        }
      });
    });
  }

  bindBeginCheckout() {
    document.addEventListener("submit", e => {
      const form = e.target;
      if (!form.action.includes("/checkout")) return;

      fetch("/cart.js")
        .then(r => r.json())
        .then(cart => this.trackBeginCheckout(cart));
    });
  }

  /* ---------------------------------------------------------
   * Helpers
   * --------------------------------------------------------- */

  generateEventId(prefix = "") {
    if (window.crypto && crypto.randomUUID) {
      return prefix + crypto.randomUUID();
    }
    return (
      prefix +
      Date.now() +
      "_" +
      Math.random().toString(36).substring(2, 9)
    );
  }

  detectPageType() {
    const path = location.pathname;
    if (/^\/products\/[^/]+/.test(path)) return "product";
    if (/^\/collections\/[^/]+/.test(path)) return "collection";
    if (path.startsWith("/cart")) return "cart";
    if (path === "/") return "index";
    return "unknown";
  }

  detectCurrency() {
    const g = window.Geolizr || window.geolizr;
    if (g?.currency) return g.currency;
    if (g?.activeCurrency) return g.activeCurrency;
    return this.config.currency;
  }

  debug(label, payload) {
    if (!this.config.debug) return;
    console.groupCollapsed(`[ShopifyDL] ${label}`);
    console.log(payload);
    console.groupEnd();
  }
}
